<template>
  <div>
    <!-- Create button -->
    <v-row>
      <v-col v-if="hasFilterableHeaders" class="text-start">
        <v-btn text small color="secondary" @click="filter = !filter">
          <v-badge
            v-if="active_filter_count > 0"
            color="red"
            :content="active_filter_count"
            bordered
          >
            {{ $t("table.filter") }}
          </v-badge>
          <span v-else>
            {{ $t("table.filter") }}
          </span>
        </v-btn>
      </v-col>
      <v-col v-if="create_route" class="text-end">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              @click="addOnClick"
              v-on="on"
              fab
              color="white"
              class="primary"
              elevation="1"
            >
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("table.click_here_to_add_new") }} {{ type }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-divider class="mt-2"></v-divider>
    <!-- The datatable -->
    <v-data-table
      class="pl-0"
      :footer-props="footerProps"
      :server-items-length="getTotalAmount"
      :options.sync="options"
      :headers="getHeaders"
      :items="getItems"
      :loading="loading"
      :sort-by="sort_by"
      :sort-desc="sort_desc"
      :custom-sort="custom_sort"
      @click:row="row_on_click"
      multi-sort
    >
      <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
      <template v-slot:item.actions="{ item }" v-if="hasActions">
        <div v-if="!hide_actions(item)">
          <v-tooltip v-if="view_route" bottom>
            <template v-slot:activator="{ on }">
              <v-btn class="" text icon @click="viewOnClick(item)">
                <v-icon class="justify-center" v-on="on">mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span v-text="$t('table.view')"></span>
          </v-tooltip>
          <v-tooltip
            v-if="(edit_route || edit_dialog) && show_edit(item)"
            bottom
          >
            <template v-slot:activator="{ on }">
              <v-btn text icon @click="editOnClick(item)">
                <v-icon v-on="on">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span v-text="$t('table.edit')"></span>
          </v-tooltip>
          <v-tooltip
            v-if="
              (dispatch_delete || custom_delete_dialog) && show_delete(item)
            "
            bottom
          >
            <template v-slot:activator="{ on }">
              <v-btn @click="openDeleteDialog(item)" class="" text icon>
                <v-icon class="justify-center" v-on="on">mdi-cancel</v-icon>
              </v-btn>
            </template>
            <span
              v-text="
                (delete_popup_text &&
                  delete_popup_text.delete_button_text &&
                  delete_popup_text.delete_button_text) ||
                  $t('table.delete')
              "
            ></span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <!-- edit dialog -->
    <v-dialog v-model="editDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t("table.edit") }}
        </v-card-title>
        <v-card-text>
          <component
            :is="edit_dialog"
            v-if="editDialog"
            :item="itemToEdit"
            :loading="editBusy"
            :submit-error="editSubmitError"
            @submit="handleEditSubmit"
            @cancel="
              editDialog = false;
              editSubmitError = null;
            "
            @clear-error="editSubmitError = null"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Delete dialog-->
    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline warning--text" style="word-break: normal"
          >{{ $t("table.attention") }}!<br />
          {{
            (delete_popup_text &&
              delete_popup_text.title &&
              delete_popup_text.title) ||
              ""
          }}
        </v-card-title>
        <v-card-text>
          <!-- Custom Delete dialog-->
          <component
            v-if="custom_delete_dialog"
            :is="custom_delete_dialog"
            :item="itemToDelete"
            :loading="deleteBusy"
            :submit_error="delete_submit_error"
            @error="delete_submit_error = $event"
            @submit="handleDeleteSubmit"
            @cancel="
              deleteDialog = false;
              delete_submit_error = null;
            "
            @clear-error="delete_submit_error = null"
          />
          <!-- Default Delete dialog-->
          <div v-else class="pa-5 my-6 text-center">
            {{
              (delete_popup_text &&
                delete_popup_text.text &&
                delete_popup_text.text) ||
                ""
            }}
            <v-card-actions>
              <v-btn
                color="warning"
                text
                @click="handleDeleteSubmit"
                :loading="deleteBusy"
                >{{
                  (delete_popup_text &&
                    delete_popup_text.delete_button_text &&
                    delete_popup_text.delete_button_text) ||
                    $t("table.delete")
                }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="grey"
                text
                @click="deleteDialog = false"
                :disabled="deleteBusy"
                >{{
                  (delete_popup_text &&
                    delete_popup_text.cancel_button_text &&
                    delete_popup_text.cancel_button_text) ||
                    $t("table.cancel")
                }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-expand-x-transition>
      <v-navigation-drawer
        :width="filterNavigationSize"
        v-if="filter"
        fixed
        right
        bottom
        mobile-breakpoint="0"
      >
        <v-row class="px-4" no-gutters>
          <v-col>
            <v-btn class="my-5" @click="removeAllFilters" color="secondary"
              >{{ $t("table.delete_all_filters") }}
            </v-btn>
          </v-col>
          <v-col cols="auto" align-self="center">
            <v-btn icon @click="filter = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-divider></v-divider>

        <v-expansion-panels accordion flat class="ml-1">
          <v-expansion-panel v-for="header in getHeaders" :key="header.value">
            <div v-if="header.search_type">
              <v-expansion-panel-header
                >{{ header.text }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div>
                  <v-row v-if="header.search_type === 'text'">
                    <v-col cols="auto" align-self="center">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-on="on" v-bind="attrs">
                            <v-icon
                              >{{ getOperatorIcon(header.search_operator) }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="item in textFilters"
                            :key="item.value"
                            @click="header.search_operator = item.value"
                          >
                            <v-icon class="mr-2"
                              >{{ getOperatorIcon(item.value) }}
                            </v-icon>
                            {{ item.text }}
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="header.search_value[0]"
                        :placeholder="$t('table.type_here')"
                        type="text"
                      />
                    </v-col>
                  </v-row>

                  <v-row v-else-if="header.search_type === 'number'">
                    <v-col cols="auto" align-self="center">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-on="on" v-bind="attrs">
                            <v-icon
                              >{{ getOperatorIcon(header.search_operator) }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="item in numberFilters"
                            :key="item.value"
                            @click="header.search_operator = item.value"
                          >
                            <v-icon class="mr-2"
                              >{{ getOperatorIcon(item.value) }}
                            </v-icon>
                            {{ item.text }}
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-col>
                    <v-col>
                      <v-text-field
                        v-model="header.search_value[0]"
                        :placeholder="$t('table.type_here')"
                        type="number"
                      />

                      <v-text-field
                        v-if="header.search_operator === 'range'"
                        v-model="header.search_value[1]"
                        :placeholder="$t('table.type_here')"
                        type="number"
                      />
                    </v-col>
                  </v-row>

                  <v-row v-else-if="header.search_type === 'datetime'">
                    <v-col cols="auto" align-self="center">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-on="on" v-bind="attrs">
                            <v-icon
                              >{{ getOperatorIcon(header.search_operator) }}
                            </v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="item in datetimeFilters"
                            :key="item.value"
                            @click="header.search_operator = item.value"
                          >
                            <v-icon class="mr-2"
                              >{{ getOperatorIcon(item.value) }}
                            </v-icon>
                            {{ item.text }}
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-col>
                  </v-row>
                </div>
              </v-expansion-panel-content>
            </div>
            <v-divider v-if="header.search_type"></v-divider>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-navigation-drawer>
    </v-expand-x-transition>
  </div>
</template>

<script>
import Vue from "vue";
import i18n from "@/i18n";
import _ from "lodash";

export default {
  name: "BaseDatatable",
  props: {
    extendas_datatable_options: {
      type: Object,
      required: false,
      default: function() {
        return { pagination_type: "offset" };
      }
    },
    row_on_click: {
      type: Function,
      required: false,
      default: () => {}
    },
    header_items: {
      type: Array,
      required: true
    },
    id_field: {
      type: String,
      required: false,
      default: "id"
    },
    user_friendly_id_field: {
      type: String,
      required: false,
      default: "id"
    },
    type: {
      type: String,
      required: true
    },
    store_state: {
      type: String,
      required: false,
      default: null
    },
    dispatch_get: {
      type: String,
      required: true
    },
    dispatch_delete: {
      type: String,
      required: false,
      default: null
    },
    delete_popup_text: {
      type: Object,
      required: false,
      default: () => ({
        title: null,
        text: null,
        delete_button_text: null,
        cancel_button_text: null
      })
    },
    view_route: {
      type: String,
      required: false,
      default: null
    },
    edit_route: {
      type: String,
      required: false,
      default: null
    },
    create_route: {
      type: String,
      required: false,
      default: null
    },
    edit_dialog: {
      type: Object,
      required: false,
      default: null
    },
    sort_by: {
      type: Array,
      required: false,
      default: () => []
    },
    sort_desc: {
      type: Array,
      required: false,
      default: () => []
    },
    custom_sort: {
      type: Function,
      required: false
    },
    show_edit: {
      type: Function,
      required: false,
      default: () => {
        return true;
      }
    },
    show_delete: {
      type: Function,
      required: false,
      default: () => {
        return true;
      }
    },
    custom_delete_dialog: {
      type: Object,
      required: false,
      default: null
    },
    hide_actions: {
      type: Function,
      required: false,
      default: function() {
        return false;
      }
    }
  },
  mounted() {
    if (this.hasActions) {
      this.header_items.push({
        text: i18n.t("table.actions"),
        value: "actions",
        align: "center",
        sortable: false
      });
    }

    // get datatable parameters from local storage
    let params = JSON.parse(localStorage.getItem(this.dispatch_get));

    if (params && this.areHeadersSameAsBefore(params)) {
      this.options = params;
      this.headers = params.fields;
    } else {
      this.headers = this.header_items;
    }

    //get data according to given dispatch route
    this.getData();
  },
  data() {
    return {
      footerProps: { itemsPerPageOptions: [10, 25, 50, 100, 250] },
      datatable_options: {},
      filter: false,
      deleteDialog: false,
      itemToDelete: {},
      deleteBusy: false,
      items: [],
      options: {},
      headers: [],
      loading: true,
      total_amount: 0,
      typing_timeout: null,
      active_filter_count: 0,
      textFilters: [
        { text: this.$t("table.like"), value: "like" },
        { text: this.$t("table.equal"), value: "eq" }
      ],
      numberFilters: [
        { text: this.$t("table.equal"), value: "eq" },
        { text: this.$t("table.greater_than"), value: "gt" },
        { text: this.$t("table.less_than"), value: "lt" },
        { text: this.$t("table.greater_or_equal_to"), value: "gte" },
        { text: this.$t("table.less_or_equal_to"), value: "lte" },
        { text: this.$t("table.between"), value: "range" }
      ],
      datetimeFilters: [
        { text: this.$t("table.equal"), value: "eq" },
        { text: this.$t("table.greater_than"), value: "gt" },
        { text: this.$t("table.less_than"), value: "lt" },
        { text: this.$t("table.greater_or_equal_to"), value: "gte" },
        { text: this.$t("table.less_or_equal_to"), value: "lte" },
        { text: this.$t("table.between"), value: "range" }
      ],
      editDialog: false,
      itemToEdit: {},
      editBusy: false,
      editSubmitError: null,
      delete_submit_error: null
    };
  },
  computed: {
    getItems() {
      return this.items;
    },
    hasActions() {
      return (
        this.edit_route ||
        this.edit_dialog ||
        this.view_route ||
        this.custom_delete_dialog ||
        this.dispatch_delete
      );
    },
    getTotalAmount() {
      return this.total_amount;
    },
    getHeaders() {
      this.headers.forEach(function(element) {
        if (!element.search_value) {
          this.$set(element, "search_value", []);
        }

        if (!element.search_operator) {
          this.$set(element, "search_operator", "like");
        }
      }, this);

      return this.headers;
    },
    params() {
      return {
        ...this.options,
        fields: this.getHeaders
      };
    },
    hasFilterableHeaders() {
      return this.headers.some(h => h.search_type);
    },
    filterNavigationSize() {
      if (this.$vuetify.breakpoint.xl) {
        return "20%";
      } else if (this.$vuetify.breakpoint.lg) {
        return "30%";
      } else if (this.$vuetify.breakpoint.md) {
        return "40%";
      } else {
        return "100%";
      }
    }
  },
  methods: {
    areHeadersSameAsBefore(local_storage_props) {
      // copy the 2 object to new variables
      // delete the search_value from both objects
      // json stringify and compare
      // deep copy to remove reference to OG variable

      let local_storage_filters = JSON.parse(
        JSON.stringify(local_storage_props.fields)
      );
      let parent_component_filters = JSON.parse(
        JSON.stringify(this.header_items)
      );
      // remove search_value from the copied variables

      local_storage_filters.forEach(function(filter) {
        delete filter.search_value;
        delete filter.search_operator;
      });
      parent_component_filters.forEach(function(filter) {
        delete filter.search_value;
        delete filter.search_operator;
      });

      return (
        JSON.stringify(local_storage_filters) ===
        JSON.stringify(parent_component_filters)
      );
    },
    addOnClick() {
      this.$router.push({
        name: this.create_route
      });
    },
    viewOnClick(item) {
      let params = {};
      params[this.id_field] = item[this.id_field];
      this.$router.push({
        name: this.view_route,
        params: params
      });
    },
    editOnClick(item) {
      if (this.edit_dialog) {
        this.itemToEdit = { ...item };
        this.editDialog = true;
      } else {
        var params = { object: item };
        params[this.id_field] = item[this.id_field];
        this.$router.push({ name: this.edit_route, params: params });
      }
    },
    handleEditSubmit(payload) {
      this.editBusy = true;
      this.editSubmitError = null;
      this.$store
        .dispatch(payload.dispatch, payload.data)
        .then(() => {
          this.editDialog = false;
          this.editSubmitError = null;
          this.getData();
        })
        .catch(error => {
          const response = error.response || {};
          const data = response.data || {};
          if (data.error) {
            this.editSubmitError = data;
          }
        })
        .finally(() => {
          this.editBusy = false;
        });
    },
    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    handleDeleteSubmit(submit_payload = null) {
      this.deleteBusy = true;
      this.delete_submit_error = null;
      let payload = submit_payload || {};
      if (!payload || !("dispatch" in payload) || !("data" in payload)) {
        payload["dispatch"] = this.dispatch_delete;
        payload["data"] = { [this.id_field]: this.itemToDelete[this.id_field] };
      }
      this.$store
        .dispatch(payload.dispatch, payload.data)
        .then(() => {
          this.delete_submit_error = null;
          this.deleteDialog = false;
        })
        .catch(error => {
          const response = error.response || {};
          const data = response.data || {};
          if (data) {
            this.delete_submit_error = data;
          }
        })
        .finally(() => {
          this.deleteBusy = false;
          this.getData();
        });
    },
    /**
     * Method to format the data by the given renderer in the header
     */
    formatData() {
      this.getHeaders.forEach(header => {
        if (header.backup_values) {
          this.items.forEach(item => {
            if (!_.get(item, header.value)) {
              let result;
              for (let backup_value of header.backup_values) {
                if (_.get(item, backup_value)) {
                  result = backup_value;
                  break;
                }
              }
              _.set(item, header.value, _.get(item, result));
            }
          });
        }

        let renderer = header.renderer;
        if (renderer) {
          this.items.forEach(item => {
            _.set(
              item,
              header.value,
              Vue.filter(renderer)(_.get(item, header.value))
            );
          });
        }
      });
    },
    /**
     * Method to get the data from the api
     * @returns {Promise<void>}
     */
    getData() {
      this.loading = true;
      this.$store
        .dispatch(this.dispatch_get, this.getUrlQuery(this.params))
        .then(response => {
          // first save the data in the store
          if (this.store_state) {
            this.$store.commit(this.store_state, response.data.items);
          }
          // save parameters in local storage
          localStorage.setItem(this.dispatch_get, JSON.stringify(this.params));
          // JSON stringify and parse so the data wont get deep cloned
          this.items = JSON.parse(JSON.stringify(response.data.items));
          this.total_amount = response.data.total_count;
          this.formatData();
          this.loading = false;
        });
    },
    getUrlQuery(params) {
      let query = "?limit=" + params.itemsPerPage;

      if (this.extendas_datatable_options.pagination_type === "page") {
        query += "&page=" + params.page;
      } else {
        query += "&offset=" + (params.page - 1) * params.itemsPerPage;
      }

      //Order
      for (let i = 0; i < params.sortBy.length; i++) {
        // remove array indexes from field. example; trade_items[0].dps_trade_items[2].description
        let order_field = params.sortBy[i].replace(/\[[0-9]\]/gi, "");
        query +=
          "&order[" +
          order_field +
          "]=" +
          (params.sortDesc[i] ? "desc" : "asc");
      }

      //Search per field
      params.fields.forEach(item => {
        if (
          item.search_value[0] !== "" &&
          item.search_value[0] !== undefined &&
          item.search_value[0] !== null
        ) {
          query += this.formatFilterQuery(item);
        }
      });

      return query;
    },
    formatFilterQuery(item) {
      // remove array indexes from field. example; trade_items[0].dps_trade_items[2].description

      let item_value = item.value.replace(/\[[0-9]\]/gi, "");

      if (item.search_operator === "range") {
        if (!item.search_value[0] || !item.search_value[1]) {
          return "";
        }

        let query =
          "&filter[" +
          item_value +
          "][gte]=" +
          this.formatQueryValue(item.search_value[0]);
        query +=
          "&filter[" +
          item_value +
          "][lte]=" +
          this.formatQueryValue(item.search_value[1]);
        return query;
      }

      return (
        "&filter[" +
        item_value +
        "][" +
        item.search_operator +
        "]=" +
        this.formatQueryValue(item.search_value[0])
      );
    },
    formatQueryValue(item_value) {
      // store values in separate var's because otherwise the watcher of the headers gets called again (double query)
      var value;
      if (item_value instanceof Date) {
        value = item_value.toISOString();
      } else {
        value = item_value;
      }

      return value;
    },
    lowercaseString(item) {
      return item.toLowerCase();
    },
    getOperatorIcon(operator) {
      let icon = "";
      switch (operator) {
        case "like":
          icon = "mdi-approximately-equal-box";
          break;
        case "eq":
          icon = "mdi-code-equal";
          break;
        case "gt":
          icon = "mdi-code-greater-than";
          break;
        case "lt":
          icon = "mdi-code-less-than";
          break;
        case "gte":
          icon = "mdi-code-greater-than-or-equal";
          break;
        case "lte":
          icon = "mdi-code-less-than-or-equal";
          break;
        case "range":
          icon = "mdi-math-norm-box";
      }
      return icon;
    },
    removeAllFilters() {
      this.headers.forEach(header => {
        header.search_value = [];
      });
    },
    stoppedTyping() {
      // reset the offset on search
      this.options.page = 1;
      // get new data
      this.getData();
    }
  },
  watch: {
    // on this.headers change -> wait 1 sec before getData
    headers: {
      handler(newValue, oldValue) {
        // count active filters for filter button
        let count = 0;
        this.headers.forEach(function(header) {
          let filled;
          if (header.search_operator === "range")
            filled = header.search_value[0] && header.search_value[1];
          else filled = header.search_value[0];

          if (filled) count++;
        });
        this.active_filter_count = count;
        // only watch this after the objects are 'initialized' & 'Actions' is added to the headers
        if (newValue.length !== oldValue.length) return;
        // reset timer
        clearTimeout(this.typing_timeout);
        // set new timeout
        this.typing_timeout = setTimeout(this.stoppedTyping, 1000);
      },
      deep: true
    },
    // on datatable options change -> getData
    options: {
      handler(newVal, oldVal) {
        // itemsPerPage gets filled after the datatable is initialized
        // so dont call the api by initialization as it is done on mounted()
        // check of the object are unequal
        if (
          oldVal.itemsPerPage !== undefined &&
          JSON.stringify(newVal) !== JSON.stringify(oldVal)
        ) {
          this.getData();
        }
      },
      deep: true
    }
  }
};
</script>
